import { DateTime } from 'luxon';
import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getHeaderRowsByHeaderType } from '../functions';
import { RootState } from '../store';
import { HeaderType, ModeTimeLineType } from '../types';
import { GantGridDatePeriod } from './datePeriod/gantGridDatePeriod';
import { GantGridDatePeriodDragMarker } from './datePeriod/gantGridDatePeriodDragMarker';
import GantChartLayout from './gantChartLayout';
import GantGrid from './gantGrid';
import GantGridFantomArrow from './gantGridFantomArrow';
import GantGridTodaySelected from './gantGridTodaySelected';
import GantHeaderGrid from './gantHeaderGrid';
import GantItemList from './gantItemList';
import GantLeftHeaderHookController from './gantLeftHeaderHookController';
import GantLeftHeaderHookDraggable from './gantLeftHeaderHookDraggable';
import GantLeftHeaderPanel from './gantLeftHeaderPanel';
import GantSearch from './gantSearch';
import { ProjectsTree } from './projectsTree';
import { ScrollProvider } from './scrollContext';
import { WidthLeftHeaderProvider } from './widthLeftColumnContext';

export interface IGantChart {
    isInputItemsExists?: boolean;
    dates: DateTime[];
    paddingBottom?: number;
}

const GantChart: React.FC<IGantChart> = ({ dates, paddingBottom = 0, isInputItemsExists }) => {
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const modeTimeLine = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);

    const headerLeftGantRef = useRef<HTMLDivElement>(null);
    const gridGantRef = useRef<HTMLDivElement>(null);

    const headerRows = getHeaderRowsByHeaderType(headerType, modeTimeLine);

    const renderShadow = () => {
        return (
            <defs>
                <filter id="shadow" x="0" y="0">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="rgba(0, 0, 0, 0.6)" />
                </filter>
            </defs>
        );
    };

    const renderShadowSelect = () => {
        return (
            <defs>
                <filter id="shadowSelect" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={'#022af1'} />
                </filter>
            </defs>
        );
    };

    const renderShadowSelectChildren = () => {
        return (
            <defs>
                <filter id="shadowSelectChildren" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={'#022af1'} />
                </filter>
            </defs>
        );
    };

    const gantGrid = useMemo(() => <GantGrid dates={dates} />, [dates]);

    const gantItemList = useMemo(() => <GantItemList />, []);

    const renderGantChart = (
        <>
            <g>
                <g>{gantGrid}</g>
                <g>
                    <GantGridDatePeriodDragMarker typeGrid={'main'} />
                </g>
                <g>{gantItemList}</g>
                <g>
                    <GantGridTodaySelected typeGrid={'main'} />
                    <GantGridDatePeriod typeGrid={'main'} />
                </g>
                <g>
                    <GantGridFantomArrow />
                </g>
            </g>
            {renderShadow()}
            {renderShadowSelect()}
            {renderShadowSelectChildren()}
        </>
    );

    return (
        <WidthLeftHeaderProvider>
            <ScrollProvider>
                <GantChartLayout
                    gridGantRef={gridGantRef}
                    headerLeftGantRef={headerLeftGantRef}
                    contentAreaLeftTop={
                        <>
                            <GantLeftHeaderHookDraggable
                                gridGantRef={gridGantRef}
                                headerLeftGantRef={headerLeftGantRef}
                            />
                            <GantLeftHeaderHookController />
                            <GantLeftHeaderPanel />
                            <GantSearch />
                        </>
                    }
                    contentAreaRightTop={<GantHeaderGrid rowsHeaderGant={headerRows} dates={dates} />}
                    contentAreaLeftBottom={<ProjectsTree />}
                    contentAreaRightBottom={renderGantChart}
                    paddingBottom={paddingBottom}
                    isInputItemsExists={isInputItemsExists}
                />
            </ScrollProvider>
        </WidthLeftHeaderProvider>
    );
};

export default GantChart;
